import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { translations } from '../i18n/translations';
import { Button } from '../components/ui/button';
import { ArrowLeft, Send, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const AccessRequest = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState('fr');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const t = (translations[lang] || translations.fr).accessRequest;

  const handleSubmit = async () => {
    if (!email.trim() || !name.trim()) return;
    setSending(true);
    setError('');

    try {
      await axios.post('/api/access-request', {
        email: email.trim(),
        name: name.trim(),
        message: message.trim(),
        language: lang,
      });
      setSent(true);
    } catch (err) {
      if (err.response?.status === 400) {
        setError(t.alreadyExists);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="access-request-success" className="min-h-screen bg-atlasBg flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-500" />
          </div>
          <p className="text-xl text-white font-semibold mb-3">{t.success}</p>
          <Button onClick={() => navigate('/')} className="btn-primary mt-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {(translations[lang] || translations.fr).accessGuard.cta}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="access-request-page" className="min-h-screen bg-atlasBg flex items-center justify-center px-6">
      <div className="absolute inset-0">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-atlasPurple/[0.04] rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-atlasTextSecondary hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          ATLAS
        </button>

        <h1 className="text-2xl font-bold text-white mb-2">{t.title}</h1>
        <p className="text-atlasTextSecondary mb-8">{t.subtitle}</p>

        <div className="glass-card p-6 space-y-5">
          <input
            data-testid="access-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-atlasBg/50 border border-atlasBorder rounded-lg px-4 py-3 text-white outline-none focus:border-atlasCyan transition-colors"
            placeholder={t.emailPlaceholder}
          />

          <input
            data-testid="access-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-atlasBg/50 border border-atlasBorder rounded-lg px-4 py-3 text-white outline-none focus:border-atlasCyan transition-colors"
            placeholder={t.namePlaceholder}
          />

          <textarea
            data-testid="access-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full bg-atlasBg/50 border border-atlasBorder rounded-lg px-4 py-3 text-white outline-none focus:border-atlasCyan transition-colors min-h-[100px] resize-none"
            placeholder={t.messagePlaceholder}
          />

          {error && (
            <div className="flex items-center gap-2 text-atlasRed text-sm">
              <AlertTriangle className="w-4 h-4" />
              {error}
            </div>
          )}

          <Button
            data-testid="access-submit-btn"
            onClick={handleSubmit}
            disabled={sending || !email.trim() || !name.trim()}
            className="w-full btn-primary py-3 flex items-center justify-center gap-2"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> {t.cta}</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccessRequest;
